// ========================================================
// FAST HIT Website — Auth Context
// Public website authentication (not admin)
// ========================================================

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import {
  type User,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { COLLECTIONS } from '../types/collections';
import type { UserProfile } from '../types';

type AuthState = {
  /** Firebase user */
  user: User | null;
  /** Firestore user profile */
  profile: UserProfile | null;
  /** Whether auth state is being resolved */
  isLoading: boolean;
  /** Whether user is signed in */
  isAuthenticated: boolean;
  /** Auth error message */
  error: string | null;
  /** Sign in */
  login: (email: string, password: string) => Promise<void>;
  /** Sign in with Google */
  loginWithGoogle: () => Promise<void>;
  /** Register */
  register: (email: string, password: string) => Promise<User>;
  /** Sign out */
  logout: () => Promise<void>;
  /** Password reset */
  resetPassword: (email: string) => Promise<void>;
  /** Send verification email */
  sendVerification: () => Promise<void>;
  /** Refresh profile from Firestore */
  refreshProfile: () => Promise<void>;
  /** Clear error */
  clearError: () => void;
};

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadProfile = useCallback(async (firebaseUser: User) => {
    try {
      const ref = doc(db, COLLECTIONS.USERS, firebaseUser.uid);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        setProfile({ id: snap.id, ...snap.data() } as UserProfile);
      } else {
        setProfile(null);
      }
    } catch (err) {
      console.error('Failed to load user profile:', err);
      setProfile(null);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser) {
        await loadProfile(firebaseUser);
      } else {
        setProfile(null);
      }
      setIsLoading(false);
    });

    return unsubscribe;
  }, [loadProfile]);

  const login = useCallback(async (email: string, password: string) => {
    try {
      setError(null);
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err: unknown) {
      const message = getAuthErrorMessage(err);
      setError(message);
      throw err;
    }
  }, []);

  const loginWithGoogle = useCallback(async () => {
    try {
      setError(null);
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      const result = await signInWithPopup(auth, provider);
      if (result.user) {
        // Ensure user profile document exists in Firestore users collection
        const userRef = doc(db, COLLECTIONS.USERS, result.user.uid);
        const snap = await getDoc(userRef);
        if (!snap.exists()) {
          await setDoc(userRef, {
            uid: result.user.uid,
            displayName: result.user.displayName || 'Cricket Player',
            email: result.user.email || '',
            photoUrl: result.user.photoURL || '',
            roles: ['player'],
            playingRole: 'batsman',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            profileCompleted: false,
            isVerified: false,
          }, { merge: true });
        }
        await loadProfile(result.user);
      }
    } catch (err: unknown) {
      const message = getAuthErrorMessage(err);
      setError(message);
      throw err;
    }
  }, [loadProfile]);

  const register = useCallback(async (email: string, password: string) => {
    try {
      setError(null);
      const result = await createUserWithEmailAndPassword(auth, email, password);
      return result.user;
    } catch (err: unknown) {
      const message = getAuthErrorMessage(err);
      setError(message);
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await signOut(auth);
      setUser(null);
      setProfile(null);
    } catch (err) {
      console.error('Logout failed:', err);
    }
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    try {
      setError(null);
      await sendPasswordResetEmail(auth, email);
    } catch (err: unknown) {
      const message = getAuthErrorMessage(err);
      setError(message);
      throw err;
    }
  }, []);

  const sendVerification = useCallback(async () => {
    if (user && !user.emailVerified) {
      await sendEmailVerification(user);
    }
  }, [user]);

  const refreshProfile = useCallback(async () => {
    if (user) {
      await loadProfile(user);
    }
  }, [user, loadProfile]);

  const clearError = useCallback(() => setError(null), []);

  const value: AuthState = {
    user,
    profile,
    isLoading,
    isAuthenticated: !!user,
    error,
    login,
    loginWithGoogle,
    register,
    logout,
    resetPassword,
    sendVerification,
    refreshProfile,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/** Map Firebase auth error codes to user-safe messages */
function getAuthErrorMessage(err: unknown): string {
  const code = (err as { code?: string })?.code;
  switch (code) {
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/user-disabled':
      return 'This account has been disabled.';
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Invalid email or password.';
    case 'auth/email-already-in-use':
      return 'An account with this email already exists.';
    case 'auth/weak-password':
      return 'Password must be at least 6 characters.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please try again later.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection.';
    default:
      return 'An unexpected error occurred. Please try again.';
  }
}

export default AuthContext;
